import { action } from 'nanostores'

import type { GetUserResponse } from '~/features/auth/types'
import type { ApiParams } from '~/lib/api'
import type { SearchParams } from '~/lib/router'

import { api } from '~/lib/api'
import { fetcher } from '~/lib/fetcher'
import { $router, isAuthExemptRoute, isProtectedRoute, isPublicRoute, redirectRoute } from '~/lib/router'

import { $token } from './token'
import { $user } from './user'
import { $inviteToken } from '~/features/workspaces/stores/invite.ts'
import type { InvitedGetUserResponse } from '~/features/auth/types'

$user.subscribe(({ isLoggedIn, token }, changedKey) => {
  if (changedKey === 'isLoggedIn') {
    const invite = $inviteToken.get()
    const page = $router.get()

    if (isLoggedIn) {
      $token.set(token)
    } else {
      $token.set(undefined)
    }

    if (isAuthExemptRoute(page?.route)) {
      return null
    }

    if (invite && isLoggedIn === true) {
      redirectRoute('joinWorkspace')
    }

    if (!isProtectedRoute(page?.route) && isLoggedIn === true && !invite) {
      // If redirected here from the OAuth consent page, go back there after login
      const params = new URLSearchParams(window.location.search)
      const returnUrl = params.get('return')
      const planFromUrl = params.get('plan')
      const planFromStorage = sessionStorage.getItem('rushdb_plan_intent')
      const plan = planFromUrl || planFromStorage
      if (plan && plan !== 'free') {
        sessionStorage.removeItem('rushdb_plan_intent')
        window.location.href = `/billing?plan=${encodeURIComponent(plan)}`
      } else if (returnUrl) {
        window.location.href = returnUrl
      } else if (page?.route !== 'oauthConsent') {
        redirectRoute('home')
      }
    }

    if (isLoggedIn === false && isProtectedRoute(page?.route)) {
      redirectRoute('signin')
    }
  }
})

$router.subscribe((page) => {
  if ($user) {
    const invite = $inviteToken.get()
    const { isLoggedIn } = $user.get()

    if (isAuthExemptRoute(page?.route)) {
      return null
    }

    if (isProtectedRoute(page?.route) && !isLoggedIn) {
      redirectRoute('signin')
    } else if (isLoggedIn && isPublicRoute(page?.route) && !invite && page?.route !== 'oauthConsent') {
      const planParam = new URLSearchParams(window.location.search).get('plan')
      if (planParam && planParam !== 'free') {
        window.location.href = `/billing?plan=${encodeURIComponent(planParam)}`
      } else {
        redirectRoute('home')
      }
    } else if (isLoggedIn && isPublicRoute(page?.route) && invite && page?.route !== 'oauthConsent') {
      redirectRoute('joinWorkspace')
    }
  }
})

export const logIn = action(
  $user,
  'logIn',
  (store, { login, password }: { login?: string; password?: string }) => {
    return fetcher<GetUserResponse['data']>(`/api/v1/auth/login`, {
      body: JSON.stringify({ login, password }),
      method: 'POST'
    }).then((user) => {
      store.set({ ...user, isLoggedIn: true })
    })
  }
)

export const logInGoogle = action($user, 'logInGoogle', (store, searchParams: SearchParams) => {
  const query = new URLSearchParams(searchParams).toString()
  return fetcher<InvitedGetUserResponse['data']>(`/api/v1/auth/google/callback?${query}`).then((user) => {
    const { workspaceId, ...restUserProps } = user
    $user.set({ ...restUserProps, isLoggedIn: true })

    if (workspaceId) {
      return workspaceId
    }
  })
})

export const logInSso = action(
  $user,
  'logInSso',
  async (store, { token, workspaceId }: { token: string; workspaceId?: string }) => {
    // The backend ACS/callback already verified the assertion and minted a
    // session token, handing it back via the redirect URL — so unlike the
    // social providers we don't call a callback endpoint, we just adopt the
    // token and hydrate the user.
    $token.set(token)
    const user = await api.user.current()
    store.set({ ...user, token, isLoggedIn: true })
    return workspaceId
  }
)

export const logInGitHub = action($user, 'logInGitHub', (store, searchParams: SearchParams) => {
  const query = new URLSearchParams(searchParams).toString()
  return fetcher<GetUserResponse['data']>(`/api/v1/auth/github/callback?${query}`).then((user) => {
    $user.set({ ...user, isLoggedIn: true })
  })
})

export const confirmEmail = action($user, 'confirmEmail', (store, searchParams: SearchParams) => {
  const query = new URLSearchParams(searchParams).toString()

  return fetcher<GetUserResponse['data']>(`/api/v1/auth/confirm?${query}`).then((user) => {
    store.set({ ...user, isLoggedIn: true })
  })
})

export const createUser = action($user, 'createUser', (store, params: ApiParams<typeof api.auth.register>) =>
  api.auth.register(params).then((user) => {
    store.set({ ...user, isLoggedIn: true })
  })
)

export const resendConfirmationLink = () => {
  const { id } = $user.get()

  if (!id) {
    throw new Error('no user id')
  }

  return api.auth.resendConfirmationLink(id)
}
